import { Row, Col, Typography, Table, Button, message, Alert } from "antd";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingAttendanceRoster,
  removeProcessingAttendanceRoster,
  resetAttendanceRosterStatus,
  saveProcessingAttendanceRoster,
} from "store/attendance-roster/attendanceRosterSlice";

const MESSAGE_KEY = "SAVE_ATTENDANCE_ROSTER";
const DEFAULT_HEADER_MAPPING =
  "userId:User ID,storeId:Store ID,phone:Phone,weekOffDays:Week Off Days,leaveDates:Leave Dates";

const SaveAttendanceRosterPage = () => {
  useDocumentTitle("Confirm Attendance Roster");
  const { processingAttendanceRoster, status, error } = useAppSelector(
    (state) => state.attendanceRoster
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isBusy = status === "loading" || status === "cancelling";

  useEffect(() => {
    const { timeUploaded, key } = processingAttendanceRoster;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) {
        message.warning("Staged upload expired. Please upload again.");
        dispatch(removeProcessingAttendanceRoster());
        navigate(`/${routes.ATTENDANCE_ROSTER.UPLOAD}`);
        return;
      }
    }
    if (!Object.keys(processingAttendanceRoster).length || !key) {
      navigate(`/${routes.ATTENDANCE_ROSTER.UPLOAD}`);
    }
  }, [dispatch, processingAttendanceRoster, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Confirming Attendance Roster...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Attendance Roster saved successfully!",
        duration: 5,
        key: MESSAGE_KEY,
      });
      navigate(`/${routes.ATTENDANCE_ROSTER.UPLOAD}`);
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling upload...", key: MESSAGE_KEY });
    } else if (status === "cancelled") {
      message.success({
        content: "Upload cancelled. Nothing was saved.",
        duration: 5,
        key: MESSAGE_KEY,
      });
      navigate(`/${routes.ATTENDANCE_ROSTER.UPLOAD}`);
    } else if (status === "error") {
      message.error({ content: error || "Something went wrong!", key: MESSAGE_KEY });
      dispatch(resetAttendanceRosterStatus());
    }
    return () => {
      message.destroy();
    };
  }, [status, error, navigate, dispatch]);

  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(DEFAULT_HEADER_MAPPING),
    []
  );

  let content;
  if (!Object.keys(processingAttendanceRoster).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingAttendanceRoster.successRows || [];
    const data = successRows.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = Object.keys(headers).map((key) => ({
      key,
      title: headers[key],
      dataIndex: key,
      render: (text: string) => <>{text ?? ""}</>,
    }));
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="key"
        scroll={{ y: 500, x: "100vh" }}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Confirm Attendance Roster" />
        </Col>
      </Row>

      {!!processingAttendanceRoster.key && (
        <Row style={{ marginBottom: 16 }}>
          <Col lg={16} xs={24}>
            <Alert
              type="info"
              message="Review staged rows, then Confirm to persist or Cancel to discard."
              description={`Total: ${processingAttendanceRoster.total ?? 0}, Success: ${
                processingAttendanceRoster.success ?? 0
              }. Staged data expires in ~15 minutes.`}
              showIcon
              banner
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={24}>{content}</Col>
      </Row>

      {!!processingAttendanceRoster.key && (
        <Row style={{ marginTop: "20px", gap: "10px" }}>
          <Col>
            <Button
              type="primary"
              onClick={() => dispatch(saveProcessingAttendanceRoster())}
              loading={status === "loading"}
              disabled={isBusy}
            >
              Confirm
            </Button>
          </Col>
          <Col>
            <Button
              onClick={() => dispatch(cancelProcessingAttendanceRoster())}
              loading={status === "cancelling"}
              disabled={isBusy}
              danger
            >
              Cancel
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SaveAttendanceRosterPage;
